import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import type { Profile } from './types';

/**
 * Who follows this profile, and who it follows.
 *
 * ## Two cards rather than one with tabs
 *
 * They are different questions - "who listens to them" and "who do they
 * listen to" - and a tab makes you press something to find out the second.
 * Both fit, because each card is one row of faces rather than a list.
 *
 * ## Faces, not names
 *
 * A follower list rendered as text is a column of usernames nobody reads. The
 * whole value of putting this on a profile is recognition: you scan it, spot
 * somebody you know, and that tells you something about the artist that no
 * count can. So the avatar is the control and the name sits under it, and the
 * card holds a single row - eight faces you might recognise beats forty you
 * scroll past.
 *
 * ## It renders nothing rather than rendering nothing much
 *
 * A card saying "0 followers" on somebody's profile is a worse thing to publish
 * than no card. The count is already on the stat strip for anybody who wants
 * the number, so an empty side is simply absent - and a profile with neither
 * draws no section at all.
 */
interface FollowRow {
  username: string;
  display_name: string;
  avatar: string | null;
}

/** One row of faces. Eight is what fits without wrapping at the column's width. */
const SHOWN = 8;

export function FollowCards({
  profile,
  only,
}: {
  profile: Profile;
  /*
   * Which card to draw. The two are separate sections in the profile's
   * arrangement - somebody can put their followers at the top and the accounts
   * they follow at the bottom - so each is asked for on its own rather than the
   * pair being one block that moves together.
   */
  only?: 'followers' | 'following';
}) {
  /*
   * A switch each, checked here rather than only by the caller, so a profile
   * that has turned one off never issues that card's request whichever way this
   * component is reached.
   */
  const followers = profile.show_followers && only !== 'following' && (
    <FollowCard
      profile={profile}
      direction="followers"
      title={copy['noun.follower.plural']}
      count={profile.follower_count}
    />
  );

  const following = profile.show_following && only !== 'followers' && (
    <FollowCard
      profile={profile}
      direction="following"
      title="Following"
      count={profile.following_count}
    />
  );

  if (only) return (followers || following) || null;

  return (
    <div className="flex flex-col gap-6">
      {followers}
      {following}
    </div>
  );
}

function FollowCard({
  profile,
  direction,
  title,
  count,
}: {
  profile: Profile;
  direction: 'followers' | 'following';
  title: string;
  count: number;
}) {
  const { data } = useQuery({
    queryKey: ['follow-card', profile.username, direction],
    queryFn: () =>
      api.get<{ items: FollowRow[] }>(
        `/users/${encodeURIComponent(profile.username)}/${direction}?limit=${SHOWN}`,
      ),
    /*
     * Not asked for at all when there is nobody to show. The count comes down
     * with the profile, so an empty side costs no request rather than one that
     * returns an empty list.
     */
    enabled: count > 0,
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.items ?? [];

  if (count === 0 || rows.length === 0) return null;

  return (
    <section aria-labelledby={`follows-${direction}`} className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3 border-b border-line pb-3">
        <h2 id={`follows-${direction}`} className="text-lg font-bold tracking-tight text-ink">
          {title}
          <span className="ml-2 font-mono text-[11px] font-normal text-faint">
            {count.toLocaleString()}
          </span>
        </h2>

        {/*
          Always, not only when there is more than is shown.
          
          It was conditional at first, on the reasoning that "See all" over six
          of six leads to a page showing the same six. But then it appears and
          disappears depending on how many followers somebody has, which makes
          the header unstable across profiles - and on a young platform it would
          almost never be there at all. A predictable affordance beats avoiding
          one redundant click.
        */}
        <Link
          to={`/${profile.username}/${direction}`}
          data-see-all={direction}
          className="meta shrink-0 text-soft transition-colors hover:text-ink"
        >
          See all
        </Link>
      </div>

      <Card className="p-4">
        <ul className="grid grid-cols-4 gap-x-2 gap-y-4 sm:grid-cols-4">
          {rows.map((row) => (
            <li key={row.username}>
              <Link
                to={`/${row.username}`}
                data-follow-card={row.username}
                className="group flex flex-col items-center gap-1.5 text-center"
              >
                {row.avatar ? (
                  <img
                    src={row.avatar}
                    alt=""
                    loading="lazy"
                    className="h-12 w-12 rounded-[var(--radius-pill)] object-cover"
                  />
                ) : (
                  /*
                    An initial rather than a placeholder image: a grid of
                    identical silhouettes is harder to scan than a grid of
                    different letters, and the point of this card is scanning.
                  */
                  <span className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-pill)] bg-raised text-sm font-bold text-soft">
                    {(row.display_name || row.username).charAt(0).toUpperCase()}
                  </span>
                )}

                <span className="w-full truncate text-[11px] text-soft transition-colors group-hover:text-ink">
                  {row.display_name || row.username}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
